import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface Poll {
  id: string;
  interview_id: string;
  question: string;
  option_positive: string | null;
  option_negative: string | null;
  created_at: string;
  updated_at: string;
}

export interface PollResponse {
  id: string;
  poll_id: string;
  user_id?: string;
  session_id?: string;
  response: string;
  created_at: string;
}

export const usePoll = (interviewId: string) => {
  const [poll, setPoll] = useState<Poll | null>(null);
  const [responses, setResponses] = useState<PollResponse[]>([]);
  const [userResponse, setUserResponse] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  // Generate session ID for anonymous users
  const sessionId = !user ? `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` : undefined;

  useEffect(() => {
    if (interviewId) {
      loadPoll();
    }
  }, [interviewId]);

  useEffect(() => {
    if (poll?.id) {
      loadResponses();
      loadUserResponse();
    }
  }, [poll?.id, user]);

  const loadPoll = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // First try to get existing poll
      const { data: existingPoll, error: pollError } = await supabase
        .from('polls')
        .select('*')
        .eq('interview_id', interviewId)
        .single();

      if (existingPoll) {
        setPoll(existingPoll);
      } else {
        // Generate new poll using edge function
        const { data, error } = await supabase.functions.invoke('generate-poll', {
          body: { interviewId }
        });

        if (error) throw error;
        
        if (data?.poll) {
          setPoll(data.poll);
        } else {
          throw new Error('Failed to generate poll');
        }
      }
    } catch (err) {
      console.error('Error loading poll:', err);
      setError('Kunne ikke laste inn avstemning');
    } finally {
      setIsLoading(false);
    }
  };

  const loadResponses = async () => {
    if (!poll?.id) return;

    try {
      const { data, error } = await supabase
        .from('poll_responses')
        .select('*')
        .eq('poll_id', poll.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setResponses(data || []);
    } catch (err) {
      console.error('Error loading responses:', err);
    }
  };

  const loadUserResponse = async () => {
    if (!poll?.id) return;

    try {
      let query = supabase
        .from('poll_responses')
        .select('response')
        .eq('poll_id', poll.id);

      if (user) {
        query = query.eq('user_id', user.id);
      } else if (sessionId) {
        query = query.eq('session_id', sessionId);
      }

      const { data, error } = await query.single();

      if (data && !error) {
        setUserResponse(data.response);
      }
    } catch (err) {
      // User hasn't responded yet, which is fine
      console.log('No existing response found');
    }
  };

  const submitResponse = async (response: string) => {
    if (!poll?.id || !response.trim()) {
      setError('Vennligst skriv inn ditt svar');
      return false;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Check if user already responded
      if (userResponse) {
        // Update existing response
        let query = supabase
          .from('poll_responses')
          .update({ response: response.trim() })
          .eq('poll_id', poll.id);

        if (user) {
          query = query.eq('user_id', user.id);
        } else if (sessionId) {
          query = query.eq('session_id', sessionId);
        }

        const { error } = await query;
        if (error) throw error;
      } else {
        // Create new response
        const { error } = await supabase
          .from('poll_responses')
          .insert({
            poll_id: poll.id,
            user_id: user?.id || null,
            session_id: sessionId || null,
            response: response.trim()
          });

        if (error) throw error;
      }

      setUserResponse(response.trim());
      await loadResponses(); // Refresh responses
      return true;
    } catch (err) {
      console.error('Error submitting response:', err);
      setError('Kunne ikke lagre ditt svar');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    poll,
    responses,
    userResponse,
    isLoading,
    isSubmitting,
    error,
    submitResponse,
    loadPoll
  };
};